import React from "react";
import { Button, Progress } from "antd";
import { ArrowLeftOutlined, ArrowRightOutlined, CheckOutlined } from "@ant-design/icons";

import PlanStep from "./steps/PlanStep";
import MoneyStep from "./steps/MoneyStep";
import EligibilityStep from "./steps/EligibilityStep";
import HygieneStep from "./steps/HygieneStep";
import FamiliesStep from "./steps/FamiliesStep";
import ReviewStep from "./steps/ReviewStep";

/**
 * Surface 1 — guided intake, for the one call where the plan gets recorded.
 *
 * Steps follow the paper form's order, which is the order a carrier rep answers
 * in: who the plan is, then the money, then who is covered, then the hygiene
 * block, then the families.
 *
 * Steps are FREELY CLICKABLE and never gated. A rep volunteers information out
 * of order and will not wait while someone completes step 2, so the rail reports
 * how full each step is rather than locking the ones ahead.
 */

const STEPS = [
  { key: "plan", label: "Plan & carrier", fields: ["employer", "carrier", "policyNumber", "certificateNumber", "accountNumber", "assignment"] },
  { key: "money", label: "Maximums & money", fields: ["feeGuide", "specialistFee", "maxBasic", "maxMajor", "deductibleSingle", "renewsMonth"] },
  { key: "eligibility", label: "Eligibility & ages", fields: ["dependentAge", "ftStudentAge", "nitrous", "examLimitations"] },
  { key: "hygiene", label: "Hygiene & recall", fields: ["recallCycle", "scalingUnits", "prevScalingPct", "oeMonths", "panMonths", "bwMonths", "sealants", "ohi", "fluoride", "compositesMolars"] },
  { key: "families", label: "Procedure families", fields: [] },
  { key: "review", label: "Shortform & review", fields: [] },
];

/** How much of a step has been answered — drives the rail's per-step meter. */
function completion(stepKey, state) {
  const step = STEPS.filter(function (s) { return s.key === stepKey; })[0];
  if (stepKey === "families") {
    const fams = state.families || {};
    const keys = Object.keys(fams);
    if (!keys.length) return 0;
    const filled = keys.filter(function (k) { return fams[k] && String(fams[k].pct || "").trim(); });
    return Math.round((filled.length / keys.length) * 100);
  }
  if (stepKey === "review") return String(state.exceptions || "").trim() || state.verifiedBy ? 100 : 0;
  const bucket = state[stepKey] || {};
  if (!step.fields.length) return 0;
  const filled = step.fields.filter(function (f) {
    const v = bucket[f];
    return v !== undefined && v !== null && String(v).trim() !== "";
  });
  return Math.round((filled.length / step.fields.length) * 100);
}

export default function IntakeFlow({ state, setState, stepIndex, setStepIndex }) {
  const step = STEPS[stepIndex];

  function setBucket(key, value) {
    setState(Object.assign({}, state, { [key]: value }));
  }

  function body() {
    if (step.key === "plan") return <PlanStep value={state.plan || {}} onChange={function (v) { setBucket("plan", v); }} />;
    if (step.key === "money") return <MoneyStep value={state.money || {}} onChange={function (v) { setBucket("money", v); }} />;
    if (step.key === "eligibility") return <EligibilityStep value={state.eligibility || {}} onChange={function (v) { setBucket("eligibility", v); }} />;
    if (step.key === "hygiene") return <HygieneStep value={state.hygiene || {}} onChange={function (v) { setBucket("hygiene", v); }} />;
    if (step.key === "families") {
      return (
        <FamiliesStep
          values={state.families || {}}
          carrier={(state.plan || {}).carrier}
          onChange={function (v) { setBucket("families", v); }}
          frequency={state.frequency || {}}
          onFrequencyChange={function (v) { setBucket("frequency", v); }}
        />
      );
    }
    return <ReviewStep state={state} onChange={setState} />;
  }

  return (
    <div className="bw-layout">
      <div className="bw-rail">
        {STEPS.map(function (s, i) {
          const pct = completion(s.key, state);
          const done = pct >= 100;
          return (
            <button
              key={s.key}
              type="button"
              className={"bw-step" + (i === stepIndex ? " is-active" : "") + (done ? " is-done" : "")}
              onClick={function () { setStepIndex(i); }}
            >
              <span className="bw-step-num">{done ? <CheckOutlined /> : i + 1}</span>
              <span className="bw-step-label">
                {s.label}
                <span className="bw-step-meta">
                  {s.fields.length || s.key === "families"
                    ? (pct ? pct + "% answered" : "not started")
                    : (pct ? "done" : "pending")}
                </span>
              </span>
            </button>
          );
        })}
        <div style={{ padding: "12px 12px 0" }}>
          <Progress
            percent={Math.round(
              STEPS.reduce(function (acc, s) { return acc + completion(s.key, state); }, 0) / STEPS.length
            )}
            size="small"
            strokeColor="#6b43b5"
          />
        </div>
      </div>

      <div>
        {body()}
        <div className="bw-actions">
          <Button
            icon={<ArrowLeftOutlined />}
            disabled={stepIndex === 0}
            onClick={function () { setStepIndex(Math.max(0, stepIndex - 1)); }}
          >
            Back
          </Button>
          <Button
            type="primary"
            disabled={stepIndex === STEPS.length - 1}
            onClick={function () { setStepIndex(Math.min(STEPS.length - 1, stepIndex + 1)); }}
          >
            Next <ArrowRightOutlined />
          </Button>
        </div>
      </div>
    </div>
  );
}
